import React, {
    Fragment,
    FormEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
    SetStateAction,
} from 'react';
import {
    Box,
    IconButton,
    useTheme,
    InputBase,
    Tooltip,
    lighten,
} from '@material-ui/core';
import { OptionsMenu } from '../Conversation';
import { FileText, Mic, Send } from 'react-feather';
import { useWarboxMessage, useWarboxConversation } from 'src/hooks';
import useAuth from 'src/hooks/useAuth';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
    CONTACT_AUTH,
    CREATE_PERMISSION,
    READ_PERMISSION,
} from 'src/constants/permissions';
import { isPermissionGranted } from 'src/utils/permissions/isPermissionGranted';
import {
    canCreateNoteConversation,
    canReadNoteConversation,
} from 'src/views/OmniChannel/utils/noteConversationPermissions';
import { ModalTemplates } from 'src/modules/conversations/components/modalTemplates';
import { CURRENT_TEMPLATE_DEFAULT } from 'src/modules/conversations/constants/threads';
import { TEMPLATE_CONTEXTS_TYPES } from 'src/modules/conversations/constants/templates';
import {
    OUTCOME_DIRECTION,
    WHATSAPP_CHANNEL,
} from 'src/constants/conversations';
import type { TDocumentation } from 'src/modules/conversations/components/types';
import type { TOutboundMultimediaItem } from 'src/views/OmniChannel/types/outboundMultimedia';
import { generateFileKey } from 'src/modules/conversations/helpers/generateFileKey';
import {
    buildOutboundPreviewItems,
    getDocumentationSendMedia,
    getPresentationCardSendMedia,
    revokeAllLocalFilePreviewUrls,
    revokeLocalFilePreviewUrl,
    toMultimediaPreviewFromOutboundItem,
    uploadLocalFileForMessage,
    type TMultimediaPreview,
} from 'src/views/OmniChannel/utils/outboundMultimedia';
import { useAudioRecorder } from 'src/views/OmniChannel/hooks/useAudioRecorder';
import { AlertsConversations } from './AlertsConversations';
import { DocumentationModal } from './documentation';
import { AudioRecorderBar } from './audio';
import { RegisterContactDialog } from './RegisterContactDialog';
import { CONTACT_DETAILS_ROUTE } from '../../constants/contactRegistration';
import {
    OutboundMultimediaDropzone,
    OutboundMultimediaPreview,
    OutboundMultimediaPreviewModal,
    type TOutboundMultimediaDropzoneHandle,
} from './multimedia';
import { getIntensityColor } from '../../utils';
import {
    OUTBOUND_MULTIMEDIA_ACCEPTED_TYPES,
    OUTBOUND_MULTIMEDIA_MAX_SIZE_BYTES,
} from 'src/views/OmniChannel/constants/outboundMultimedia';

export const SendMessage = ({
    conversation,
    handleFilesSelected,
    localFiles,
    setLocalFiles,
}: {
    conversation: any;
    handleFilesSelected: (files: File[]) => void;
    localFiles: File[];
    setLocalFiles: React.Dispatch<SetStateAction<File[]>>;
}) => {
    const theme = useTheme();
    const { sendTemplateMessage, sendTextMessage } = useWarboxMessage();
    const { user } = useAuth();
    const { updateConversation } = useWarboxConversation();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const history = useHistory();
    const dropzoneRef = useRef<TOutboundMultimediaDropzoneHandle>(null);
    const localFilesRef = useRef<File[]>([]);
    const localPreviewUrlCacheRef = useRef<Map<string, string>>(new Map());
    const templateAlertAnimationTimerRef = useRef<number | null>(null);
    const audioRecorder = useAudioRecorder();

    const [message, setMessage] = useState('');
    const [isNoteMode, setIsNoteMode] = useState(false);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [isTemplateAlertAnimating, setIsTemplateAlertAnimating] =
        useState(false);
    const [currentTemplate, setCurrentTemplate] = useState(
        CURRENT_TEMPLATE_DEFAULT
    );
    const [isSending, setIsSending] = useState(false);
    const [documentation, setDocumentation] = useState<TDocumentation[]>([]);
    const [openDocumentationModal, setOpenDocumentationModal] = useState(false);
    const [openRegisterContact, setOpenRegisterContact] = useState(false);
    const [previewItem, setPreviewItem] = useState<TMultimediaPreview | null>(
        null
    );
    const intensityColor = getIntensityColor(theme.palette.type);

    localFilesRef.current = localFiles;

    const outboundPreviewItems = useMemo(
        () =>
            buildOutboundPreviewItems(
                documentation,
                localFiles,
                localPreviewUrlCacheRef.current
            ),
        [documentation, localFiles]
    );

    const isBlocked = conversation?.status === 'blocked';
    const isChannelEnabled = conversation?.canSendMessages !== false;
    const hasActiveCustomerResponse = Boolean(
        conversation?.messages?.some((item: any) => {
            if (item.origin !== 'customer') return false;

            const createdAt = new Date(item.createdAt).getTime();
            const twentyFourHours = 24 * 60 * 60 * 1000;

            return (
                Number.isFinite(createdAt) &&
                Date.now() - createdAt < twentyFourHours
            );
        })
    );
    const canSendConversationContent =
        isChannelEnabled && hasActiveCustomerResponse;
    const linkedContactId = conversation?.linkedContactId;
    const canCreateContact =
        isPermissionGranted({
            permissions: user?.permissions || {},
            permission: CONTACT_AUTH,
            crud: CREATE_PERMISSION,
        }) && !linkedContactId;
    const canViewLinkedContact = Boolean(
        linkedContactId &&
            isPermissionGranted({
                permissions: user?.permissions || {},
                permission: CONTACT_AUTH,
                crud: READ_PERMISSION,
            })
    );
    const isChatBotActive = Boolean(
        conversation?.isChatBotActive ?? conversation?.isUsingChatBot
    );
    const presentationCardUrl = conversation?.presentationCardUrl?.trim();
    const hasPresentationCard = Boolean(presentationCardUrl);
    const hasMessage = Boolean(message.trim());
    const hasOutboundMultimedia = outboundPreviewItems.length > 0;
    const isAudioActive = audioRecorder.isActive;
    const canCreateNote = canCreateNoteConversation(user);
    const canReadNote = canReadNoteConversation(user);

    const canSend = isNoteMode
        ? Boolean(
              hasMessage &&
                  conversation?._id &&
                  user?._id &&
                  canCreateNote &&
                  !isSending
          )
        : Boolean(
              (hasMessage ||
                  hasOutboundMultimedia ||
                  audioRecorder.canSendRecording) &&
                  conversation?._id &&
                  conversation?.storeId &&
                  conversation?.area &&
                  conversation?.phone &&
                  canSendConversationContent &&
                  !isBlocked &&
                  !isSending
          );

    const clearOutboundMultimedia = () => {
        revokeAllLocalFilePreviewUrls(localPreviewUrlCacheRef.current);
        setDocumentation([]);
        setLocalFiles([]);
    };

    const resetComposer = () => {
        setMessage('');
        setIsNoteMode(false);
        clearOutboundMultimedia();
        audioRecorder.discardRecording();
    };

    useEffect(() => {
        resetComposer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversation?._id]);

    useEffect(
        () => () => {
            revokeAllLocalFilePreviewUrls(localPreviewUrlCacheRef.current);

            if (templateAlertAnimationTimerRef.current !== null) {
                window.clearTimeout(templateAlertAnimationTimerRef.current);
            }
        },
        []
    );

    const handleBlockedInputClick = () => {
        if (
            isBlocked ||
            !isChannelEnabled ||
            hasActiveCustomerResponse ||
            isNoteMode
        ) {
            return;
        }

        if (templateAlertAnimationTimerRef.current !== null) {
            window.clearTimeout(templateAlertAnimationTimerRef.current);
        }

        setIsTemplateAlertAnimating(true);
        templateAlertAnimationTimerRef.current = window.setTimeout(() => {
            setIsTemplateAlertAnimating(false);
            templateAlertAnimationTimerRef.current = null;
        }, 3000);
    };

    const handleRemoveOutboundItem = (itemId: string) => {
        if (itemId.startsWith('doc-')) {
            const documentId = itemId.replace('doc-', '');
            setDocumentation((current) =>
                current.filter((item) => item._id !== documentId)
            );
            return;
        }

        const fileKey = itemId.replace('local-', '');

        setLocalFiles((current) => {
            const file = current.find(
                (item) => generateFileKey(item) === fileKey
            );

            if (file) {
                revokeLocalFilePreviewUrl(
                    file,
                    localPreviewUrlCacheRef.current
                );
            }

            return current.filter((item) => generateFileKey(item) !== fileKey);
        });
    };

    const handlePreviewOutboundItem = (item: TOutboundMultimediaItem) => {
        setPreviewItem(toMultimediaPreviewFromOutboundItem(item));
    };

    const handleOpenDocumentation = () => {
        if (isBlocked || !canSendConversationContent || isAudioActive) return;
        setOpenDocumentationModal(true);
    };

    const handleOpenFilePicker = () => {
        if (isBlocked || !canSendConversationContent || isAudioActive) return;
        dropzoneRef.current?.open();
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
        if (
            isNoteMode ||
            isBlocked ||
            !canSendConversationContent ||
            isSending ||
            isAudioActive
        ) {
            return;
        }

        const pastedImages = Array.from(event.clipboardData.items)
            .filter(
                (item) => item.kind === 'file' && item.type.startsWith('image/')
            )
            .map((item) => item.getAsFile())
            .filter((file): file is File => Boolean(file));

        if (pastedImages.length === 0) return;

        event.preventDefault();

        const acceptedTypes = OUTBOUND_MULTIMEDIA_ACCEPTED_TYPES.split(',');
        const validImages = pastedImages.filter(
            (file) =>
                acceptedTypes.includes(file.type) &&
                file.size <= OUTBOUND_MULTIMEDIA_MAX_SIZE_BYTES
        );

        if (validImages.length !== pastedImages.length) {
            enqueueSnackbar(t('Errors.SizeErrorMessage'), {
                variant: 'error',
            });
        }

        if (validImages.length > 0) handleFilesSelected(validImages);
    };

    const handleOpenRegisterContact = () => {
        if (isAudioActive) return;
        setOpenRegisterContact(true);
    };

    const handleOpenLinkedContact = () => {
        if (!linkedContactId) return;
        history.push(`${CONTACT_DETAILS_ROUTE}?contactId=${linkedContactId}`);
    };

    const handleStartRecording = async () => {
        if (isBlocked || !canSendConversationContent || isSending) return;

        try {
            await audioRecorder.startRecording();
        } catch (error) {
            let errorMessage = 'Errors.MicrophonePermissionDenied';

            if (error instanceof DOMException) {
                if (error.name === 'NotAllowedError') {
                    errorMessage = 'Errors.MicrophonePermissionDenied';
                } else if (error.name === 'NotFoundError') {
                    errorMessage = 'Errors.MicrophonePermissionDenied';
                }
            } else if (
                error instanceof Error &&
                error.message.startsWith('Errors.')
            ) {
                errorMessage = error.message;
            }

            enqueueSnackbar(t(errorMessage), { variant: 'error' });
        }
    };

    const handleToggleNoteMode = () => {
        if (isSending) return;

        setIsNoteMode((current) => {
            const next = !current;

            if (next) {
                clearOutboundMultimedia();
                audioRecorder.discardRecording();
            }

            return next;
        });
    };

    const handleToggleChatBot = async () => {
        if (isBlocked || !isChannelEnabled || !conversation?._id || isSending) {
            return;
        }

        await updateConversation(
            { isChatBotActive: !isChatBotActive },
            conversation._id
        );
    };

    const handleSendPresentationCard = async () => {
        if (
            !presentationCardUrl ||
            isBlocked ||
            !canSendConversationContent ||
            !conversation?._id ||
            isSending
        ) {
            return;
        }

        setIsSending(true);

        try {
            await sendTextMessage({
                conversationId: conversation._id,
                storeId: conversation.storeId,
                area: conversation.area,
                senderPhone: conversation.senderPhone,
                recipientPhone: conversation.phone,
                media: getPresentationCardSendMedia(presentationCardUrl),
            });

            enqueueSnackbar(t('SnackBar.MessageSent'), {
                variant: 'success',
            });
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Errors.CannotSendWsp';

            enqueueSnackbar(t(errorMessage), {
                variant: 'error',
            });
        } finally {
            setIsSending(false);
        }
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!canSend) return;

        setIsSending(true);

        try {
            const text = message.trim();

            if (isNoteMode) {
                await sendTextMessage({
                    conversationId: conversation._id,
                    message: text,
                    kind: 'note',
                    sentBy: user._id,
                    sentByName: user.name,
                    persistInState: canReadNote,
                });

                resetComposer();
                return;
            }

            let messageSent = false;

            if (
                audioRecorder.status === 'recording' ||
                audioRecorder.status === 'paused'
            ) {
                const audioFile = await audioRecorder.stopAndGetFile();

                if (!audioFile || audioFile.size === 0) {
                    throw new Error('Errors.UnsupportedAudioFormat');
                }

                const media = await uploadLocalFileForMessage(audioFile);

                await sendTextMessage({
                    conversationId: conversation._id,
                    storeId: conversation.storeId,
                    area: conversation.area,
                    senderPhone: conversation.senderPhone,
                    recipientPhone: conversation.phone,
                    ...(text && { message: text }),
                    media,
                });

                messageSent = true;
            }

            if (hasOutboundMultimedia) {
                for (const document of documentation) {
                    const includeMessage = !messageSent && Boolean(text);
                    const media = getDocumentationSendMedia(document);

                    await sendTextMessage({
                        conversationId: conversation._id,
                        storeId: conversation.storeId,
                        area: conversation.area,
                        senderPhone: conversation.senderPhone,
                        recipientPhone: conversation.phone,
                        ...(includeMessage && { message: text }),
                        media,
                    });

                    messageSent = true;
                }

                for (const file of localFiles) {
                    const includeMessage = !messageSent && Boolean(text);
                    const media = await uploadLocalFileForMessage(file);

                    await sendTextMessage({
                        conversationId: conversation._id,
                        storeId: conversation.storeId,
                        area: conversation.area,
                        senderPhone: conversation.senderPhone,
                        recipientPhone: conversation.phone,
                        ...(includeMessage && { message: text }),
                        media,
                    });

                    messageSent = true;
                }
            } else if (!messageSent && text) {
                await sendTextMessage({
                    conversationId: conversation._id,
                    storeId: conversation.storeId,
                    area: conversation.area,
                    senderPhone: conversation.senderPhone,
                    recipientPhone: conversation.phone,
                    message: text,
                });
            }

            resetComposer();
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Errors.CannotSendWsp';

            enqueueSnackbar(t(errorMessage), {
                variant: 'error',
            });
        } finally {
            setIsSending(false);
        }
    };

    const templateConversation = {
        _id: conversation?._id,
        name: conversation?.name,
        store: conversation?.rawConversation?.store ||
            conversation?.store || {
                _id: conversation?.storeId,
                name: conversation?.storeName,
                areas: [],
            },
        channel: WHATSAPP_CHANNEL,
        direction: OUTCOME_DIRECTION,
        to: conversation?.phone,
        from: conversation?.senderPhone || '',
        area: conversation?.area,
        phone: conversation?.phone,
        senderPhone: conversation?.senderPhone,
        lead: conversation?.rawConversation?.lead,
    };

    const handleSendTemplate = async (templatePayload: any) => {
        try {
            await sendTemplateMessage({
                ...templatePayload,
                conversationId: conversation._id,
                storeId: conversation.storeId,
                area: conversation.area,
                senderPhone: conversation.senderPhone,
                recipientPhone: conversation.phone,
            });
            setIsTemplateModalOpen(false);
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : 'Errors.CannotSendWsp';

            enqueueSnackbar(t(errorMessage), {
                variant: 'error',
            });
        }
    };
    const showMicButton =
        !isNoteMode &&
        !hasMessage &&
        !hasOutboundMultimedia &&
        !isAudioActive &&
        canSendConversationContent &&
        !isBlocked;

    return (
        <Fragment>
            <AlertsConversations
                isBlocked={isBlocked}
                isChannelEnabled={isChannelEnabled}
                hasActiveCustomerResponse={hasActiveCustomerResponse}
                isTemplateAlertAnimating={isTemplateAlertAnimating}
                onSendTemplate={() => setIsTemplateModalOpen(true)}
            />

            <OutboundMultimediaDropzone
                ref={dropzoneRef}
                disabled={
                    isBlocked ||
                    !canSendConversationContent ||
                    isSending ||
                    isAudioActive
                }
                onFilesSelected={handleFilesSelected}
            />

            <DocumentationModal
                open={openDocumentationModal}
                setOpen={setOpenDocumentationModal}
                storeId={conversation?.storeId}
                documentation={documentation}
                setDocumentation={setDocumentation}
            />

            <OutboundMultimediaPreviewModal
                open={Boolean(previewItem)}
                setOpen={(isOpen) => {
                    if (!isOpen) setPreviewItem(null);
                }}
                preview={previewItem}
            />

            <RegisterContactDialog
                open={openRegisterContact}
                onClose={() => setOpenRegisterContact(false)}
                conversationId={conversation?._id}
                name={conversation?.name}
                phone={conversation?.phone}
                storeId={conversation?.storeId}
            />

            <OutboundMultimediaPreview
                items={isNoteMode ? [] : outboundPreviewItems}
                onRemove={handleRemoveOutboundItem}
                onPreview={handlePreviewOutboundItem}
            />

            <Box
                component="form"
                style={{
                    paddingTop: 10,
                    paddingBottom: 10,
                    paddingLeft: 8,
                    paddingRight: 8,
                    backgroundColor: theme.palette.background.paper,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    borderTop:
                        hasOutboundMultimedia || isAudioActive || isNoteMode
                            ? 'none'
                            : `1px solid ${theme.palette.divider}`,
                }}
                onSubmit={handleSubmit}
            >
                {!isAudioActive && !isNoteMode && (
                    <OptionsMenu
                        disabled={isBlocked || !isChannelEnabled}
                        canCreateContact={canCreateContact}
                        canViewLinkedContact={canViewLinkedContact}
                        onSendTemplate={() => setIsTemplateModalOpen(true)}
                        onOpenDocumentation={handleOpenDocumentation}
                        onOpenFilePicker={handleOpenFilePicker}
                        onOpenCreateContact={handleOpenRegisterContact}
                        onOpenLinkedContact={handleOpenLinkedContact}
                        hasPresentationCard={hasPresentationCard}
                        onSendPresentationCard={handleSendPresentationCard}
                    />
                )}

                {!isAudioActive && canCreateNote && (
                    <Tooltip title={t('Chat.AddNote')}>
                        <IconButton
                            color="primary"
                            onClick={handleToggleNoteMode}
                            disabled={isSending}
                            style={
                                isNoteMode
                                    ? {
                                          color: lighten(
                                              theme.palette.warning.dark,
                                              intensityColor
                                          ),
                                          backgroundColor:
                                              theme.palette.action.hover,
                                      }
                                    : undefined
                            }
                        >
                            <FileText />
                        </IconButton>
                    </Tooltip>
                )}

                {isAudioActive ? (
                    <AudioRecorderBar
                        status={audioRecorder.status}
                        durationMs={audioRecorder.durationMs}
                        previewUrl={audioRecorder.previewUrl}
                        waveformLevels={audioRecorder.waveformLevels}
                        canResumeRecording={audioRecorder.canResumeRecording}
                        onDiscard={audioRecorder.discardRecording}
                        onPause={audioRecorder.pauseRecording}
                        onResume={audioRecorder.resumeRecording}
                    />
                ) : (
                    <Box
                        style={{
                            flex: 1,
                            cursor:
                                !isNoteMode &&
                                isChannelEnabled &&
                                !hasActiveCustomerResponse &&
                                !isBlocked
                                    ? 'pointer'
                                    : 'default',
                        }}
                        onClick={handleBlockedInputClick}
                    >
                        <InputBase
                            disabled={
                                isNoteMode
                                    ? isSending
                                    : !canSendConversationContent || isBlocked
                            }
                            value={message}
                            onChange={(event) => setMessage(event.target.value)}
                            onPaste={handlePaste}
                            style={{
                                marginLeft: 1,
                                width: '100%',
                                pointerEvents:
                                    !isNoteMode &&
                                    isChannelEnabled &&
                                    !hasActiveCustomerResponse &&
                                    !isBlocked
                                        ? 'none'
                                        : 'auto',
                            }}
                            placeholder={
                                isNoteMode
                                    ? t('Chat.AddNote2')
                                    : t('Chat.WriteMessage')
                            }
                        />
                    </Box>
                )}

                {showMicButton && (
                    <Tooltip title={t('Chat.RecordAudio')}>
                        <IconButton
                            color="primary"
                            onClick={handleStartRecording}
                            disabled={isSending}
                        >
                            <Mic />
                        </IconButton>
                    </Tooltip>
                )}
                {!isNoteMode && (
                    <Tooltip title={t('Chat.SendMessage')}>
                        <IconButton
                            color="primary"
                            disabled={!canSend}
                            type="submit"
                        >
                            <Send />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            <ModalTemplates
                open={isTemplateModalOpen}
                setOpen={setIsTemplateModalOpen}
                currentTemplate={currentTemplate}
                setCurrentTemplate={setCurrentTemplate}
                conversation={templateConversation as any}
                contextType={TEMPLATE_CONTEXTS_TYPES.CONVERSATION}
                handleSendTemplate={handleSendTemplate}
                contextKey={conversation?._id}
            />
        </Fragment>
    );
};
